import type { Creator } from '@/types/portal';

/** The creator fields matching needs (demo creators and real profiles both have them). */
type MatchableCreator = Pick<Creator, 'contentFormats' | 'city' | 'markets'>;
import { contentTypeOptions, type AttachmentKind } from './types';

export { onboardingLink } from '@/utils/campaign';

/** Hard cap per attachment (50 MB). */
export const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;

/** Extensions treated as documents when the browser reports no usable MIME type. */
const DOCUMENT_EXTENSIONS = [
  'pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx',
  'txt', 'csv', 'md', 'rtf', 'odt', 'ods', 'odp', 'key', 'numbers', 'pages',
];

/** Resolve the attachment category from a file's MIME type, falling back to its extension. */
export function getAttachmentKind(mimeType: string, fileName: string): AttachmentKind | null {
  const mime = (mimeType || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('text/') || mime === 'application/pdf') return 'document';
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  if (DOCUMENT_EXTENSIONS.includes(ext)) return 'document';
  if (mime.startsWith('application/')) return 'document';
  return null;
}

/** Human-readable file size (e.g. 240 KB, 1.6 MB). */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Content-type ids that can actually match a creator (excludes the free-text "other"). */
export function matchableContentTypes(selected: string[]): string[] {
  return selected.filter((id) => id !== 'other');
}

function tokenize(value: string): string[] {
  return value
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/**
 * Map a free-text custom format to the content-type ids it is most related to,
 * so creators producing those formats can be ranked higher.
 */
export function customFormatAffinity(customText: string): string[] {
  const text = customText.trim().toLowerCase();
  if (!text) return [];
  const words = tokenize(text);
  return contentTypeOptions
    .filter((option) => option.id !== 'other')
    .filter((option) => {
      const label = option.label.toLowerCase();
      if (text.includes(label) || label.includes(text)) return true;
      return option.keywords.some((keyword) => {
        const key = keyword.toLowerCase();
        if (text.includes(key)) return true;
        return tokenize(key).some((word) => words.includes(word));
      });
    })
    .map((option) => option.id);
}

/** How many of the custom format's related content types this creator produces. */
export function creatorCustomAffinity(creator: MatchableCreator, customText: string): string[] {
  return customFormatAffinity(customText).filter((id) => creator.contentFormats.includes(id));
}

/** The content-type ids this creator offers that also match the brief selection. */
export function matchedFormats(creator: MatchableCreator, selected: string[]): string[] {
  if (selected.length === 0) return creator.contentFormats;
  return creator.contentFormats.filter((id) => selected.includes(id));
}

/** The creator's home country, derived from their listed city (e.g. "Kampala, Uganda" -> Uganda). */
export function creatorHomeMarket(creator: MatchableCreator): string {
  const parts = creator.city.split(',');
  return (parts[parts.length - 1] ?? '').trim();
}

/** Is the creator actually based in one of the selected markets? */
export function basedInSelectedMarkets(creator: MatchableCreator, selected: string[]): boolean {
  if (selected.length === 0) return true;
  return selected.includes(creatorHomeMarket(creator));
}

/** Does the creator cover (reach into) at least one of the selected markets? */
export function coversSelectedMarkets(creator: MatchableCreator, selected: string[]): boolean {
  if (selected.length === 0) return true;
  return creator.markets.some((market) => selected.includes(market));
}

/** Human-readable labels for a list of content-type ids. */
export function formatLabels(ids: string[]): string {
  return ids
    .map((id) => contentTypeOptions.find((type) => type.id === id)?.label)
    .filter((label): label is string => Boolean(label))
    .join(', ');
}

export interface MessageValues {
  creator: string;
  campaign: string;
  brand: string;
  description: string;
  deliverables: string;
  contentTypes: string;
  rate: string;
}

/** Resolve the invite template placeholders for a single creator. */
export function personalizeMessage(template: string, values: MessageValues): string {
  return template
    .replace(/\{\{creator\}\}/g, values.creator)
    .replace(/\{\{campaign\}\}/g, values.campaign)
    .replace(/\{\{brand\}\}/g, values.brand)
    .replace(/\{\{description\}\}/g, values.description.trim())
    .replace(/\{\{deliverables\}\}/g, values.deliverables.trim())
    .replace(/\{\{contentTypes\}\}/g, values.contentTypes)
    .replace(/\{\{rate\}\}/g, values.rate)
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}